import { isCamFile } from '@/workers/camFiles';
import { CamExtractedData } from '@/workers/camTypes';

export type ParsedCam = { fileId: string; data: CamExtractedData } | null;

const isArt = (name: string) => /\.art$/i.test(name.trim());
const hasBoard = (p: ParsedCam) => Boolean(p?.data.material?.width);

/** The first of `files` that parses with a board size, else the first that
 * parsed at all. Sequential: the first file nearly always answers. */
function firstParsed<F>(
  files: F[],
  parse: (f: F) => Promise<ParsedCam>
): Promise<ParsedCam> {
  return files.reduce<Promise<ParsedCam>>(async (prev, f) => {
    const best = await prev;
    if (hasBoard(best)) return best;
    const parsed = await parse(f);
    return hasBoard(parsed) ? parsed : (best ?? parsed);
  }, Promise.resolve(null));
}

/**
 * The design file Job details read, with the other package's file of the same
 * design attached as its `companion` for the Tools step.
 *
 * A Vectric file leads when it knows its board: it holds the sizes the
 * designer typed and the only XY origin, where Carveco's copy drifts (24.984
 * for 25). Tools read the Carveco file first — see `toolSources`.
 */
export async function chooseCam<F extends { name: string }>(
  files: F[],
  parse: (f: F) => Promise<ParsedCam>
): Promise<ParsedCam> {
  const cams = files.filter((f) => isCamFile(f.name));
  // A relief model holds no job or toolpaths, so it must not outrank a design.
  const design = async (f: F): Promise<ParsedCam> => {
    const p = await parse(f);
    return p?.data.format === 'v3m' ? null : p;
  };
  const [vectric, carveco] = await Promise.all([
    firstParsed(
      cams.filter((f) => !isArt(f.name)),
      design
    ),
    firstParsed(
      cams.filter((f) => isArt(f.name)),
      parse
    ),
  ]);
  const vectricLeads = hasBoard(vectric) || !hasBoard(carveco);
  const main = vectricLeads ? (vectric ?? carveco) : carveco;
  const other = main === vectric ? carveco : vectric;
  if (!main || !other) return main;
  return { ...main, data: { ...main.data, companion: other } };
}

/** The parsed data for one design file: the main one or its companion. */
export function camDataFor(
  id: string | null,
  cam: CamExtractedData | null,
  mainId: string | null
): CamExtractedData | null {
  if (id === null) return null;
  if (id === mainId) return cam;
  const companion = cam?.companion;
  return companion?.fileId === id ? companion.data : null;
}
